/** @fileoverview Top bar tabs of an app: as many as fit, with the rest in a "More" menu. */

import { useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type AppNavTab = { id: string; label: string; selectionPath?: string };

const GAP = 4;
const TAB_CLASS =
  'h-full shrink-0 border-b-2 px-3 text-sm font-medium transition-colors whitespace-nowrap';

/**
 * Picks the tabs shown in the bar. All of them when they fit; otherwise the first ones that leave
 * room for the "More" button, with the selected tab taking the last place when it would be hidden.
 * @param widths width of each tab, in order
 * @param moreWidth width of the "More" button
 * @param available width of the bar
 * @param selectedIndex index of the selected tab, or -1
 * @returns indexes of the tabs to show, in display order
 */
const pickVisible = (
  widths: number[],
  moreWidth: number,
  available: number,
  selectedIndex: number
) => {
  const all = widths.map((_, index) => index);
  const total =
    widths.reduce((sum, width) => sum + width, 0) + GAP * Math.max(widths.length - 1, 0);
  if (total <= available) return all;
  const budget = available - moreWidth - GAP;
  let used = 0;
  let count = 0;
  while (count < widths.length && used + widths[count] + (count ? GAP : 0) <= budget) {
    used += widths[count] + (count ? GAP : 0);
    count += 1;
  }
  if (selectedIndex < 0 || selectedIndex < count) return all.slice(0, count);
  return [...all.slice(0, Math.max(count - 1, 0)), selectedIndex];
};

/**
 * Shows the tabs of an app. Tabs that do not fit move into a "More" menu instead of scrolling.
 * @param tabs the tabs in order
 * @param selectedId id of the selected tab
 * @param onSelect called with the id of a tab the user picks
 */
export default function AppNavTabs({
  tabs,
  selectedId,
  onSelect,
}: {
  tabs: AppNavTab[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const navRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleIds, setVisibleIds] = useState<string[] | null>(null);
  const selectedIndex = tabs.findIndex(({ id }) => id === selectedId);

  useLayoutEffect(() => {
    const nav = navRef.current;
    const measure = measureRef.current;
    if (!nav || !measure) return;
    const update = () => {
      const widths = [...measure.children].map((child) => child.getBoundingClientRect().width);
      const moreWidth = widths.pop() ?? 0;
      const picked = pickVisible(widths, moreWidth, nav.clientWidth, selectedIndex);
      setVisibleIds(picked.map((index) => tabs[index].id));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [tabs, selectedIndex]);

  const shownIds = visibleIds ?? tabs.map(({ id }) => id);
  const shown = shownIds.flatMap((id) => tabs.find((tab) => tab.id === id) ?? []);
  const hidden = tabs.filter(({ id }) => !shownIds.includes(id));

  return (
    <nav
      ref={navRef}
      className="relative ml-3 flex h-full min-w-0 flex-1 items-center gap-1 overflow-hidden"
      aria-label="App navigation"
    >
      {shown.map((tab) => (
        <button
          key={tab.id}
          type="button"
          aria-current={selectedId === tab.id ? 'page' : undefined}
          data-fabrik-selection-path={tab.selectionPath}
          className={`${TAB_CLASS} ${
            selectedId === tab.id
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => onSelect(tab.id)}
        >
          {tab.label}
        </button>
      ))}
      {hidden.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={`${TAB_CLASS} inline-flex items-center gap-1 border-transparent text-muted-foreground hover:text-foreground`}
            aria-label={`${hidden.length} more screens`}
          >
            More
            <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums">
              {hidden.length}
            </span>
            <ChevronDown className="size-3.5" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            sideOffset={0}
            className="w-auto min-w-44 gap-0 overflow-hidden p-1"
          >
            {hidden.map((tab) => (
              <DropdownMenuItem
                key={tab.id}
                data-fabrik-selection-path={tab.selectionPath}
                className="rounded-none px-4 py-2.5 text-sm font-medium whitespace-nowrap text-muted-foreground focus:bg-foreground/[0.07] focus:text-foreground not-last:border-b not-last:border-b-border/50"
                onClick={() => onSelect(tab.id)}
              >
                {tab.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {/* Unseen copy of every tab and of the widest "More" button, read to decide what fits. */}
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute top-0 left-0 flex h-full items-center gap-1"
        aria-hidden="true"
      >
        {tabs.map((tab) => (
          <span key={tab.id} className={`${TAB_CLASS} flex items-center`}>
            {tab.label}
          </span>
        ))}
        <span className={`${TAB_CLASS} flex items-center gap-1`}>
          More <span className="px-1.5 text-xs">{tabs.length}</span>
          <ChevronDown className="size-3.5" />
        </span>
      </div>
    </nav>
  );
}
